import RedocParametersRegion from "../redocParametersRegion/redocParamersRegion/redocParametersRegion";
import React, { Fragment, useRef, useState, useEffect } from "react";
import RedocParamTextArea from "../redocParamBody/redocParamBody";
import RedocParametersClientId from "../redocParametersRegion/redocParametersClientId/redocParametersClientId";
import GetToken from "../redocGetTokenForm/getToken";
import {
  getTryoutSettings,
  saveTryoutResizePosition,
  saveTryoutLabelWidth,
} from "../../../utils/tryoutStorage";

const TryOutForm = (props) => {
  const paramsContainer = useRef();
  const formContainer = useRef();
  const tableRef = useRef();

  // Load saved resize position from local storage
  const savedSettings = getTryoutSettings();
  const initialParamsWidth = savedSettings.resizePosition || 50;
  const initialLabelWidth = savedSettings.labelWidth || 40;

  const [paramsHeight, setParamsHeight] = useState(null);
  const [paramsWidth, setParamsWidth] = useState(initialParamsWidth); // Percentage width for parameters section
  const [bodyWidth, setBodyWidth] = useState(100 - initialParamsWidth); // Percentage width for body section
  const [isResizingParams, setIsResizingParams] = useState(false);
  const [labelWidth, setLabelWidth] = useState(initialLabelWidth); // Percentage width for label column
  const [isResizingLabel, setIsResizingLabel] = useState(false);

  useEffect(() => {
    calcHeight();
  });

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizingParams) return;

      const containerRect = formContainer.current?.getBoundingClientRect();
      if (!containerRect) return;

      const newParamsWidth =
        ((e.clientX - containerRect.left) / containerRect.width) * 100;

      // Limit the width to reasonable bounds (20% to 80%)
      if (newParamsWidth >= 20 && newParamsWidth <= 80) {
        setParamsWidth(newParamsWidth);
        setBodyWidth(100 - newParamsWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizingParams(false);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";

      // Save the resize position to local storage
      saveTryoutResizePosition(paramsWidth);
    };

    if (isResizingParams) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizingParams, paramsWidth]);

  // Label column resize logic
  useEffect(() => {
    const handleLabelMouseMove = (e) => {
      if (!isResizingLabel) return;

      const tableRect = tableRef.current?.getBoundingClientRect();
      if (!tableRect) return;

      const newLabelWidth =
        ((e.clientX - tableRect.left) / tableRect.width) * 100;

      // Limit the width to reasonable bounds (20% to 60%)
      if (newLabelWidth >= 20 && newLabelWidth <= 60) {
        setLabelWidth(newLabelWidth);
      }
    };

    const handleLabelMouseUp = () => {
      setIsResizingLabel(false);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";

      // Save the label width to local storage
      saveTryoutLabelWidth(labelWidth);
    };

    if (isResizingLabel) {
      document.addEventListener("mousemove", handleLabelMouseMove);
      document.addEventListener("mouseup", handleLabelMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    }

    return () => {
      document.removeEventListener("mousemove", handleLabelMouseMove);
      document.removeEventListener("mouseup", handleLabelMouseUp);
    };
  }, [isResizingLabel, labelWidth]);

  const calcHeight = () => {
    setTimeout(() => {
      if (paramsContainer.current) {
        setParamsHeight(paramsContainer.current.offsetHeight);
      }
    }, 500);
  };

  const handleParamsResizeStart = (e) => {
    e.preventDefault();
    setIsResizingParams(true);
  };

  const handleLabelResizeStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizingLabel(true);
  };

  const {
    parameterErrors,
    handleParameterChange,
    parameters,
    handleInputChange,
    method,
    form,
    security,
    documentationId,
    applications,
    swaggerJson,
    path,
    applyToken,
  } = props;
  const getRegionErrors = (region) => {
    return (
      parameterErrors && parameterErrors.filter((param) => param.in === region)
    );
  };

  let { securitySchemes } = swaggerJson ? swaggerJson.components : {};

  let availableFlows = securitySchemes
    ? Object.keys(securitySchemes)
        .map((key) => securitySchemes[key])
        .filter((r) => r.type === "oauth2")
    : [];

  // Get the security flow according to path
  // security options or the global default security options include on swagger

  const getSecurityFlow = () => {
    const securityOptions = swaggerJson.paths[path][method].security
      ? swaggerJson.paths[path][method].security
      : swaggerJson.security;

    // Convert array of options to objects
    let security = securityOptions.reduce((acc, val, index) => {
      return (acc[index] = val);
    }, {});

    // Create array of the keys depending on the security scheme of the swagger object
    const authFlow = Object.keys(securitySchemes).filter((key) => {
      return security[key] !== undefined && security[key].length > 0;
    });

    return authFlow;
  };

  // Check if we're in horizontal layout by checking if body exists and parent has horizontal class
  const isHorizontal = form.body && props.horLayout;

  return (
    <form
      ref={formContainer}
      style={
        isHorizontal
          ? {
              position: "relative",
              display: "flex",
              height: "100%",
              alignItems: "stretch",
            }
          : { position: "relative" }
      }
    >
      <div
        ref={paramsContainer}
        className="parameters-container"
        style={
          isHorizontal
            ? {
                width: `${paramsWidth}%`,
                flexShrink: 0,
                position: "relative",
                paddingRight: "10px",
                height: "100%",
                overflowY: "auto",
              }
            : {}
        }
      >
        {(parameters && parameters.length) ||
        (security && security.length > 0) ? (
          <table
            ref={tableRef}
            style={{ position: "relative", tableLayout: "fixed" }}
          >
            <colgroup>
              <col style={{ width: `${labelWidth}%` }} />
              <col style={{ width: `${100 - labelWidth}%` }} />
            </colgroup>
            <thead>
              <tr>
                <th colSpan={2} style={{ position: "relative" }}>
                  <div
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <div>PARAMETERS</div>
                    {availableFlows.length > 0 && securitySchemes && (
                      <div style={{ width: "50%" }}>
                        <GetToken
                          securityFlow={getSecurityFlow()}
                          clientId={form.header["Client-Id"]}
                          swaggerJson={swaggerJson}
                          applyToken={applyToken}
                        />
                      </div>
                    )}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {security && security.length > 0 ? (
                <Fragment>
                  <RedocParametersClientId
                    parameters={security.filter(
                      (item) => item.name === "Client-Id"
                    )}
                    errors={getRegionErrors("header")}
                    values={form.header}
                    onChange={handleParameterChange}
                    documentationId={documentationId}
                    applications={applications}
                    productUuid={props.productUuid}
                  />

                  <RedocParametersRegion
                    /*TODO: remove client id from security*/
                    onChange={handleParameterChange}
                    title="Security"
                    values={form.header}
                    parameters={security.filter(
                      (item) => item.name !== "Client-Id"
                    )}
                    errors={getRegionErrors("header")}
                  />
                </Fragment>
              ) : null}
              {parameters && parameters.length > 0 ? (
                <>
                  {Object.keys(form).map((paramIn, index) => (
                    <RedocParametersRegion
                      key={paramIn + ":" + index}
                      onChange={handleParameterChange}
                      title={paramIn}
                      values={form[paramIn]}
                      parameters={parameters.filter(
                        (param) => param.in === paramIn
                      )}
                      errors={getRegionErrors(paramIn)}
                    />
                  ))}
                </>
              ) : null}
            </tbody>

            {/* Horizontal Resize Handle for Label Column */}
            <div
              className="tryout-form__label-resize-handle"
              onMouseDown={handleLabelResizeStart}
              style={{
                position: "absolute",
                top: 0,
                left: `${labelWidth}%`,
                transform: "translateX(-4px)",
                width: "8px",
                height: "100%",
                cursor: "col-resize",
                zIndex: 5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  width: "1px",
                  height: "100%",
                  backgroundImage:
                    "linear-gradient(to bottom, rgba(0, 212, 255, 0.4) 50%, transparent 50%)",
                  backgroundSize: "1px 8px",
                  backgroundRepeat: "repeat-y",
                }}
              />
            </div>
          </table>
        ) : null}
      </div>

      {/* Vertical Resize Handle for Parameters */}
      {isHorizontal && (
        <div
          className="tryout-form__resize-handle"
          onMouseDown={handleParamsResizeStart}
          style={{
            width: "4px",
            height: "100%",
            cursor: "col-resize",
            background: "rgba(0, 212, 255, 0.2)",
            flexShrink: 0,
          }}
        />
      )}

      {method &&
      (method.toLowerCase() === "post" ||
        method.toLowerCase() === "patch" ||
        method.toLowerCase() === "put") ? (
        <div
          style={
            isHorizontal
              ? {
                  width: `${bodyWidth}%`,
                  flexShrink: 0,
                  paddingLeft: "10px",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }
              : {}
          }
        >
          <RedocParamTextArea
            height={isHorizontal ? null : paramsHeight}
            title="Body"
            name="body"
            value={form.body}
            onChange={handleInputChange}
          />
        </div>
      ) : null}
    </form>
  );
};

export default TryOutForm;
